import { useEffect, useState } from "react";
import { ChatWidget } from "@/components/chat/ChatWidget";

export default function EmbeddedChat() {
  const [companyId, setCompanyId] = useState<string>("");
  const [companyName, setCompanyName] = useState<string>("");
  const [primaryColor, setPrimaryColor] = useState<string>("#4f46e5");
  const [textColor, setTextColor] = useState<string>("#ffffff");
  const [userEmail, setUserEmail] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const id = params.get("companyId");
      const name = params.get("companyName");
      const primary = params.get("primaryColor");
      const text = params.get("textColor");
      const email = params.get("userEmail");

      if (!id) {
        setError("Missing companyId parameter");
        setLoading(false);
        return;
      }

      setCompanyId(id);
      if (name) setCompanyName(decodeURIComponent(name));
      if (primary) setPrimaryColor(decodeURIComponent(primary));
      if (text) setTextColor(decodeURIComponent(text));
      if (email) setUserEmail(decodeURIComponent(email));
      
      if (window.parent !== window) {
        try {
          window.parent.postMessage({ type: "cmp:widget:loaded" }, "*");
          console.log("Sent loaded message to parent");
        } catch (e) {
          console.error("Error sending loaded message to parent:", e);
        }
      }
      
      setLoading(false);
    } catch (err) {
      console.error("Error initializing chat widget:", err);
      setError("Failed to initialize chat widget");
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-muted-foreground">Loading chat widget...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      <style dangerouslySetInnerHTML={{
        __html: `
          html, body {
            margin: 0;
            padding: 0;
            height: 100%;
            overflow: hidden;
            background: transparent;
          }
          :root {
            --primary-color: ${primaryColor};
            --primary-foreground: ${textColor};
          }
          /* Improve scrollbar styling */
          ::-webkit-scrollbar {
            width: 8px;
          }
          ::-webkit-scrollbar-track {
            background: rgba(0, 0, 0, 0.05);
            border-radius: 4px;
          }
          ::-webkit-scrollbar-thumb {
            background: rgba(0, 0, 0, 0.2);
            border-radius: 4px;
          }
          ::-webkit-scrollbar-thumb:hover {
            background: rgba(0, 0, 0, 0.3);
          }
          /* Word breaking for messages */
          .message-content {
            word-break: break-word;
            overflow-wrap: break-word;
          }
          /* Image styling */
          .chat-image {
            max-width: 100%;
            border-radius: 8px;
            cursor: pointer;
            transition: transform 0.2s;
          }
          .chat-image:hover {
            transform: scale(1.02);
          }
          /* Make sure the knowledge base content doesn't overflow */
          .knowledge-base-content {
            max-height: 100%;
            overflow-y: auto;
          }
          /* Improve knowledge base styling in embedded mode */
          .knowledge-base-article {
            overflow-y: auto;
            padding-right: 8px;
          }
          /* Ensure proper scrolling in embedded mode */
          #root {
            height: 100%;
            overflow: hidden;
          }
        `
      }} />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <ChatWidget
          companyId={companyId}
          companyName={companyName || "Support Chat"}
          embedded={true}
          onClose={handleClose}
          primaryColor={primaryColor}
          textColor={textColor}
          userEmail={userEmail}
        />
      </div>
    </div>
  );

  function handleClose() {
    if (window.parent !== window) {
      try {
        window.parent.postMessage({ type: "cmp:widget:close" }, "*");
        console.log("Sent close message to parent");
      } catch (e) {
        console.error("Error sending close message to parent:", e);
      }
    }
  }
}
